import React, { useEffect, useState } from 'react';
import { authFetch } from './authSession';
import { API_BASE } from './config/api';
import './CopyLinkPage.css';

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function CopyLinkPage({ isOpen, onClose, publicId, token }) {
  const [isPublic, setIsPublic] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const authToken = token || localStorage.getItem('authToken') || '';

  const campaignLink = (() => {
    if (typeof window === 'undefined') return '';
    const { origin, pathname } = window.location;
    if (publicId && pathname.includes(`/${publicId}/`)) {
      return `${origin}${pathname}`;
    }
    return publicId ? `${origin}/${publicId}` : `${origin}/campaigns`;
  })();

  const loadPermissions = async () => {
    if (!publicId || !authToken) return;
    setIsLoadingSettings(true);
    setError('');
    setSuccessMessage('');
    try {
      const response = await authFetch(`${API_BASE}/campaigns/${publicId}/permissions`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Не удалось загрузить настройки доступа');
      }

      setIsPublic(Boolean(data?.isPublic));
      const emails = Array.isArray(data?.canAccess) ? data.canAccess : [];
      setSelectedEmails(emails.filter((email) => typeof email === 'string' && email.trim()));
    } catch (e) {
      setError(e.message || 'Ошибка загрузки настроек доступа');
    } finally {
      setIsLoadingSettings(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, publicId]);

  const addEmail = () => {
    const value = emailInput.trim().toLowerCase();
    if (!isValidEmail(value)) {
      setError('Введите корректный email');
      return;
    }
    if (selectedEmails.includes(value)) {
      setEmailInput('');
      return;
    }
    setSelectedEmails((prev) => [...prev, value]);
    setEmailInput('');
    setError('');
    setSuccessMessage('');
  };

  const removeEmail = (email) => {
    setSelectedEmails((prev) => prev.filter((e) => e !== email));
    setSuccessMessage('');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(campaignLink);
      setSuccessMessage('Ссылка скопирована');
    } catch {
      setError('Не удалось скопировать ссылку');
    }
  };

  const handleSaveSettings = async () => {
    if (!publicId) return false;

    setIsSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      const response = await authFetch(`${API_BASE}/campaigns/${publicId}/permissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          isPublic,
          canAccess: selectedEmails,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || 'Не удалось сохранить настройки доступа');
      }

      const normalizedEmails = Array.isArray(data?.canAccess) ? data.canAccess : selectedEmails;
      setSelectedEmails(normalizedEmails);
      setIsPublic(Boolean(data?.isPublic ?? isPublic));
      setSuccessMessage('Настройки доступа сохранены');
      return true;
    } catch (e) {
      setError(e.message || 'Ошибка сохранения настроек доступа');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndCopy = async () => {
    const isSaved = await handleSaveSettings();
    if (!isSaved) return;
    await handleCopyLink();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="cl-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cl-head">
          <div>
            <h2 className="cl-title">Доступ по ссылке</h2>
            <p className="cl-subtitle">Настройте, кто сможет открыть кампанию</p>
          </div>
          <button type="button" className="cl-close" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>

        <div className="cl-body">
          {isLoadingSettings ? (
            <div className="cl-loading">Загружаем настройки доступа...</div>
          ) : (
            <>
              <div className="cl-row">
                <span className="cl-row-label">Публичная ссылка</span>
                <button
                  type="button"
                  className={`cl-switch${isPublic ? ' is-on' : ''}`}
                  onClick={() => setIsPublic((prev) => !prev)}
                  aria-pressed={isPublic}
                  aria-label="Публичная ссылка"
                >
                  <span className="cl-switch-knob" />
                </button>
              </div>

              <p className="cl-hint">
                {isPublic
                  ? 'Любой пользователь со ссылкой сможет просматривать кампанию.'
                  : 'Доступ будет только у пользователей из списка email.'}
              </p>

              {!isPublic && (
                <div>
                  <div className="cl-email-row">
                    <input
                      className="cl-input"
                      type="email"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addEmail();
                        }
                      }}
                      placeholder="Введите email"
                    />
                    <button type="button" className="cl-btn-secondary" onClick={addEmail}>
                      Добавить
                    </button>
                  </div>

                  <div className="cl-chips">
                    {selectedEmails.length === 0 ? (
                      <div className="cl-chips-empty">Пока нет адресов с доступом</div>
                    ) : (
                      selectedEmails.map((email) => (
                        <div key={email} className="cl-chip">
                          <span>{email}</span>
                          <button
                            type="button"
                            className="cl-chip-remove"
                            onClick={() => removeEmail(email)}
                            aria-label={`Удалить ${email}`}
                          >
                            ×
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              <div className="cl-link-box">
                <p className="cl-link-label">Ссылка на кампанию</p>
                <div className="cl-link-row">
                  <input className="cl-link-input" type="text" value={campaignLink} readOnly />
                  <button type="button" className="cl-btn-copy" onClick={handleCopyLink}>
                    Копировать
                  </button>
                </div>
              </div>

              {error && <div className="cl-error">{error}</div>}
              {successMessage && <div className="cl-success">{successMessage}</div>}
            </>
          )}
        </div>

        <div className="cl-foot">
          <button
            type="button"
            className="cl-btn-ghost"
            onClick={onClose}
            disabled={isSaving}
          >
            Отмена
          </button>
          <button
            type="button"
            className="cl-btn-primary"
            onClick={handleSaveAndCopy}
            disabled={isSaving || isLoadingSettings}
          >
            {isSaving ? 'Сохраняем...' : 'Сохранить и скопировать'}
          </button>
        </div>
      </div>
    </div>
  );
}
